import { Flex, useDisclosure, useToast } from "@chakra-ui/react";
import { useFormik } from "formik";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { isValidPhoneNumber } from "react-phone-number-input";
import { useLocation, useNavigate } from "react-router";
import * as Yup from "yup";

import { ASCII_ONLY_REGEX } from "@equidefi/constants/validation";
import { VaultContext } from "@equidefi/portals/context/VaultProvider";
import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import { useCurrentUser } from "@equidefi/portals/hooks/useUsers";
import { useSaveVaultProfile } from "@equidefi/portals/hooks/useVault";
import {
  capitalizeEveryWord,
  formatSecurities,
  isRegSEligible,
  offeringHidesSecurityLabel,
  offeringIsRegS,
} from "@equidefi/shared";
import { ENTITY, INDIVIDUAL, INTERNATIONAL, JOINT, US_CITIZEN } from "@equidefi/shared/constants/profile";
import { BannerAlert, Heading, Input, Modal, PhoneField, RadioGroup, Text } from "@equidefi/ui";
import { Icon } from "@equidefi/ui/icon";

import { AccountTypeCard } from "@/components/AccountTypeCard";
import { UsCitizenRadio } from "@/components/workflow/UsCitizenRadio";
import WorkflowProtection from "@/components/workflow/WorkflowProtection";
import useWorkflowPosition from "@/hooks/useWorkflowPosition";
import { useWorkflowContext } from "@/pages/workflow/context/WorkflowContext";
import { EquidefiMarkdown } from "@equidefi/portals/components/Markdown";

import { MESSAGE } from "../../../../../issuer/src/constants/forms";

import { WorkflowContainer } from "../WorkflowContainer";
import WorkflowForm from "../WorkflowForm";

const baseSchemaShape = {
  first_name: Yup.string()
    .trim()
    .nullable()
    .min(2, "First name must be at least 2 characters")
    .matches(ASCII_ONLY_REGEX, MESSAGE.ASCII_ONLY)
    .required(MESSAGE.REQUIRED("First name")),
  middle_name: Yup.string().trim().nullable().matches(ASCII_ONLY_REGEX, MESSAGE.ASCII_ONLY),
  last_name: Yup.string()
    .trim()
    .nullable()
    .min(2, "Last name must be at least 2 characters")
    .matches(ASCII_ONLY_REGEX, MESSAGE.ASCII_ONLY)
    .required(MESSAGE.REQUIRED("Last name")),
  phone_number: Yup.string()
    .nullable()
    .required(MESSAGE.REQUIRED("Mobile Phone"))
    .test({
      message: "Must be a valid phone number",
      test: (value) => (value ? isValidPhoneNumber(value) : false),
    }),
};

const regSSchemaShape = {
  is_us_citizen: Yup.string()
    .nullable()
    .when("ta_account_type", (val, s) =>
      ["INDIVIDUAL", "JOINT"].includes(val)
        ? s
            .required("Please select your citizenship")
            .oneOf([US_CITIZEN, INTERNATIONAL], "Please select your citizenship")
        : s,
    ),
  is_resident_inside_us: Yup.boolean()
    .nullable()
    .when("ta_account_type", (val, s) =>
      ["INDIVIDUAL", "JOINT"].includes(val)
        ? s.required("Please select your residency").oneOf([true, false], "Please select your residency")
        : s,
    ),
  is_registered_in_us: Yup.boolean()
    .nullable()
    .when("ta_account_type", (val, s) =>
      !["INDIVIDUAL", "JOINT"].includes(val)
        ? s.required("Please indicate registration status").oneOf([true, false], "Please indicate registration status")
        : s,
    ),
};

const getSchema = (isOfferingRegS = false) => {
  if (isOfferingRegS) {
    return Yup.object().shape({
      ...baseSchemaShape,
      ...regSSchemaShape,
    });
  }

  return Yup.object().shape({
    ...baseSchemaShape,
  });
};

const ACCOUNT_TYPE_OPTIONS = [
  {
    value: "INDIVIDUAL",
    label: "Individual",
    description: ({ offering }) =>
      `${formatSecurities({
        offering,
        capitalize: true,
      }).toString()} will be issued to you only.`,
    icon: <Icon.User size={6} />,
  },
  {
    value: "JOINT",
    label: "Joint Tenant",
    description: ({ offering }) =>
      `${formatSecurities({
        offering,
        capitalize: true,
      }).toString()} will be issued to you and another person.`,
    icon: <Icon.Users size={6} />,
  },
  {
    value: "TRUST",
    label: "Trust",
    description: ({ offering }) =>
      `${formatSecurities({
        offering,
        capitalize: true,
      }).toString()} will be issued to a trust.`,
    icon: <Icon.FileText size={6} />,
  },
  {
    value: "ENTITY",
    label: "Entity",
    description: ({ offering }) =>
      `${formatSecurities({
        offering,
        capitalize: true,
      }).toString()} will be issued to an entity, such as a Corporation, LLC, etc.`,
    icon: <Icon.Briefcase size={6} />,
  },
];

export function WorkflowProfile() {
  const { investment, offering, refetchInvestment } = useWorkflowContext();
  const { refetch: refetchVault } = useContext(VaultContext);
  const location = useLocation();
  const { data: user, refetch: refetchUser } = useCurrentUser();
  const navigate = useNavigate();
  useWorkflowPosition(1);
  useDocumentTitle(["Profile", offering?.name, investment?.issuer?.name]);

  const saveVaultProfile = useSaveVaultProfile(investment?.id);
  const toast = useToast();
  const [spinner, showSpinner] = useState(false);
  const isOfferingRegS = offeringIsRegS(offering);
  const hideSecurityLabel = offeringHidesSecurityLabel(offering);
  const regSBlockedReason = offering?.settings?.reg_s_blocked_reason;
  const regSEligibilityModal = useDisclosure();

  const onSubmit = useCallback(
    async (data) => {
      const isAccountTypeChanged =
        investment.vault?.ta_account_type && investment.vault?.ta_account_type !== data.ta_account_type;

      const submittedEntityType = [INDIVIDUAL, JOINT].includes(data.ta_account_type) ? INDIVIDUAL : ENTITY;

      const entityToIndividualUs =
        investment.vault?.entity_type === ENTITY &&
        submittedEntityType === INDIVIDUAL &&
        investment.vault?.citizenship === US_CITIZEN;

      let page = "subscription";

      if (location.state?.returnTo) {
        page = location.state.returnTo;
      }

      if (isAccountTypeChanged && location.state?.returnTo) {
        // Entity → Individual (US citizen) must capture Government ID via the address step before returning to review.
        if (entityToIndividualUs) {
          page = "address";
        } else {
          page = "signers";
        }
      }

      if (!location.state?.returnTo && !investment.payment_editable) {
        page = "address";
      }

      showSpinner(true);

      // This IIFE (immediately invoked function expression) is used to keep the logic clean for citizenship value,
      // which is only relevant for Reg S offerings and is determined based on multiple fields
      const citizenship = (() => {
        if (!isOfferingRegS || !data.ta_account_type) return undefined;

        if (submittedEntityType === INDIVIDUAL) {
          return data.is_us_citizen;
        }

        if (data.is_registered_in_us === true) return US_CITIZEN;
        if (data.is_registered_in_us === false) return INTERNATIONAL;
        return undefined;
      })();

      const isRegSInvestorEligible = isRegSEligible({
        isOfferingRegS,
        entityType: submittedEntityType,
        citizenship,
        isResidentInsideUS: data.is_resident_inside_us,
        isRegisteredInUS: data.is_registered_in_us,
      });

      try {
        await saveVaultProfile.mutateAsync({
          first_name: data.first_name?.trim(),
          middle_name: data.middle_name?.trim() || null,
          last_name: data.last_name?.trim(),
          phone_number: data.phone_number,
          ta_account_type: data.ta_account_type,
          ta_sub_type: data.ta_sub_type,
          citizenship,
          entity_type: submittedEntityType,
          user_id: user.id,
          skip_transition: isOfferingRegS
            ? !isRegSInvestorEligible || location.state?.skip_transition || !investment.payment_editable
            : location.state?.skip_transition || !investment.payment_editable,
        });

        if (!isRegSInvestorEligible) {
          regSEligibilityModal.onOpen();
          showSpinner(false);
          return;
        }

        await refetchInvestment();
        await refetchVault();
        await refetchUser();
        navigate(`/offerings/${offering.slug}/${investment?.id}/${page}`);
      } catch (error) {
        console.error(error);
        toast({
          status: "error",
          description: "There was an error trying to save your profile data",
        });
        showSpinner(false);
      }
    },
    [
      saveVaultProfile,
      refetchInvestment,
      refetchVault,
      refetchUser,
      navigate,
      isOfferingRegS,
      offering.slug,
      investment?.id,
      investment.vault.ta_account_type,
      investment.vault?.entity_type,
      investment.vault?.citizenship,
      toast,
      location.state,
      user.id,
      regSEligibilityModal,
    ],
  );

  const { handleSubmit, setFieldValue, handleBlur, getFieldProps, errors, values, touched, isValid } = useFormik({
    initialValues: {
      first_name: investment.vault?.first_name,
      middle_name: investment.vault?.middle_name,
      last_name: investment.vault?.last_name,
      phone_number: investment.vault?.phone_number,
      ta_account_type: investment.vault?.ta_account_type || "INDIVIDUAL",
      is_us_citizen: investment.vault?.citizenship === US_CITIZEN ?? null,
      is_resident_inside_us: null,
      is_registered_in_us: null,
    },
    enableReinitialize: !spinner,
    validationSchema: getSchema(isOfferingRegS),
    onSubmit,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
  });

  const entityType = useMemo(() => {
    if (values.ta_account_type === "INDIVIDUAL" || values.ta_account_type === "JOINT") {
      return "INDIVIDUAL";
    }
    return "ENTITY";
  }, [values]);

  const isIndividualOrJoint = entityType === "INDIVIDUAL";
  const shouldRefresh = location.state?.refresh;

  const isAccountTypeChanged =
    investment.vault?.ta_account_type && investment.vault?.ta_account_type !== values.ta_account_type;

  useEffect(() => {
    if (shouldRefresh) {
      refetchInvestment();
    }
  }, [refetchInvestment, shouldRefresh]);

  return (
    <WorkflowContainer
      title="Investor Information"
      onContinue={handleSubmit}
      buttonProps={{
        isDisabled: !isValid,
        mt: 2,
        isLoading: spinner,
      }}>
      <WorkflowForm onSubmit={handleSubmit}>
        <WorkflowProtection vaultId={investment?.vault_id} />
        <Text>
          How should the{" "}
          {formatSecurities({
            offering,
          }).toString()}{" "}
          be issued?
        </Text>
        <Flex direction="column" gap="10px" my={4}>
          {ACCOUNT_TYPE_OPTIONS.map((option) => (
            <AccountTypeCard
              key={option.value}
              value={option.value}
              label={option.label}
              description={option.description({ offering })}
              icon={option.icon}
              isSelected={values.ta_account_type === option.value}
              onSelect={(value) => {
                setFieldValue("ta_sub_type", null);
                setFieldValue("ta_account_type", value);
              }}
            />
          ))}
        </Flex>

        {isOfferingRegS &&
          (isIndividualOrJoint ? (
            <>
              <UsCitizenRadio
                value={values.is_us_citizen}
                onChange={(value) => {
                  setFieldValue("is_us_citizen", value);
                }}
              />

              <Heading textStyle="h2" m={0} mb={2} fontWeight="700" mt={6}>
                Are you a resident inside the US?
              </Heading>
              <RadioGroup
                value={values.is_resident_inside_us}
                onChange={(value) => {
                  setFieldValue("is_resident_inside_us", value);
                }}
                direction="horizontal"
                options={[
                  { value: true, label: "Yes" },
                  { value: false, label: "No" },
                ]}
              />
            </>
          ) : (
            <>
              <Heading textStyle="h2" m={0} mb={2} fontWeight="700" mt={6}>
                Is your {capitalizeEveryWord(values.ta_account_type)} formed under US Law?
              </Heading>
              <RadioGroup
                value={values.is_registered_in_us}
                onChange={(value) => {
                  setFieldValue("is_registered_in_us", value);
                }}
                direction="horizontal"
                options={[
                  { value: true, label: "Yes" },
                  { value: false, label: "No" },
                ]}
              />
            </>
          ))}

        <Heading textStyle="h1" m={0} fontWeight="700" mt="10">
          Authorized Signer
        </Heading>
        <BannerAlert
          status="info"
          description={`Enter the legal name of one person only. ${
            values.ta_account_type !== "INDIVIDUAL" ? "You will be able to add additional signers later." : ""
          }`}
          my={4}
        />
        <Input
          label="First Name"
          isRequired
          defaultValue={investment.vault.first_name}
          error={touched.first_name && errors.first_name}
          isInvalid={errors.first_name && touched.first_name}
          {...getFieldProps("first_name")}
        />
        <Input
          label="Middle Name"
          defaultValue={investment.vault.middle_name}
          error={touched.middle_name && errors.middle_name}
          isInvalid={errors.middle_name && touched.middle_name}
          {...getFieldProps("middle_name")}
        />
        <Input
          label="Last Name"
          isRequired
          defaultValue={investment.vault.last_name}
          error={touched.last_name && errors.last_name}
          isInvalid={errors.last_name && touched.last_name}
          {...getFieldProps("last_name")}
        />
        <PhoneField
          name="phone_number"
          error={touched?.phone_number && errors?.phone_number}
          isInvalid={errors.phone_number && touched.phone_number}
          value={values.phone_number}
          onChange={(value) => setFieldValue("phone_number", value)}
          onBlur={handleBlur}
          isRequired
          label="Mobile Phone"
        />
      </WorkflowForm>
      {isAccountTypeChanged && investment.signer_count > 1 && (
        <BannerAlert
          mb="2"
          mt="4"
          description="Changing investment account type will result in all additional signers being deleted, you will have to re-add them."
          status="warning"
        />
      )}

      <Modal
        isOpen={regSEligibilityModal.isOpen}
        onClose={regSEligibilityModal.onClose}
        title="Ineligible for this Investment"
        contentProps={{
          maxW: { base: "full", md: "540px" },
          w: { base: "full", md: "540px" },
        }}
        buttons={[
          {
            label: "Go Back",
            action: regSEligibilityModal.onClose,
            colorScheme: "purple",
            variant: "solid",
          },
        ]}>
        <BannerAlert
          status="error"
          description={
            regSBlockedReason ? (
              <EquidefiMarkdown>{regSBlockedReason}</EquidefiMarkdown>
            ) : (
              "Unfortunately based on your citizenship and residency, you are not eligible to participate in this Regulation S offering."
            )
          }
          mb={4}
        />
      </Modal>
    </WorkflowContainer>
  );
}
